import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { reportService } from "../api/reportService";
import { getDateRangeForPeriod } from "../utils/salesUtils";

export default function DistributionCharts({ appliedRange }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError("");

        let period;
        if (appliedRange?.startMonth && appliedRange?.endMonth) {
          const [startYear, startMonth] = appliedRange.startMonth.split("-").map(Number);
          const [endYear, endMonth] = appliedRange.endMonth.split("-").map(Number);
          const pad = (num) => String(num).padStart(2, "0");
          const lastDay = new Date(endYear, endMonth, 0).getDate();
          period = {
            start_date: `${startYear}-${pad(startMonth)}-01`,
            end_date: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`,
          };
        } else {
          period = getDateRangeForPeriod("month");
        }

        const response = await reportService.getDashboard({
          ...period,
          pending_limit: 10,
        });

        setDashboardData(response?.data || {});
      } catch (err) {
        console.error("Failed to load distribution chart data:", err);
        setError(err?.message || "Gagal memuat data distribusi");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [appliedRange]);

  const buildDonutData = (
    items = [],
    labelKey,
    valueKey,
    qtyKey = "quantity",
  ) => {
    const rows = (items || [])
      .map((item) => ({
        label: String(item?.[labelKey] || "").trim(),
        value: Number(item?.[valueKey] || 0),
        qty: Number(item?.[qtyKey] || item?.transaction_qty || 0),
      }))
      .filter((item) => item.label && item.value > 0);

    if (!rows.length) {
      return {
        labels: ["Tidak ada data"],
        series: [0],
        qtys: [0],
      };
    }

    return {
      labels: rows.map((item) => item.label),
      series: rows.map((item) => item.value),
      qtys: rows.map((item) => item.qty),
    };
  };

  const productData = useMemo(() => {
    const items = (dashboardData?.top_products || [])
      .map((item) => ({
        label: String(item?.product_name || "").trim(),
        value: Number(item?.total_sales || 0),
        qty: Number(item?.quantity || 0),
      }))
      .filter((item) => item.label && item.qty > 0)
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);
    const max = items.length ? Math.max(...items.map((i) => i.qty)) : 1;
    return { items, max };
  }, [dashboardData]);

  const categoryData = useMemo(() => {
    return buildDonutData(
      dashboardData?.top_categories,
      "category_name",
      "total_sales",
      "quantity",
    );
  }, [dashboardData]);

  const paymentData = useMemo(() => {
    return buildDonutData(
      dashboardData?.payment_methods,
      "payment_method",
      "total_amount",
      "transaction_qty",
    );
  }, [dashboardData]);

  // Chart options base
  const formatCurrency = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "Rp 0";
    return `Rp ${num.toLocaleString("id-ID")}`;
  };

  const chartOptions = {
    chart: {
      type: "donut",
      fontFamily: "Inter, sans-serif",
    },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "12px",
              color: "#6B7280",
            },
            value: {
              show: true,
              fontSize: "14px",
              fontWeight: 700,
              color: "#111827",
              formatter: (val) => formatCurrency(val),
            },
            total: {
              show: true,
              label: "Total",
              color: "#6B7280",
              fontSize: "12px",
              formatter: (w) => {
                const total = (w?.globals?.seriesTotals || []).reduce(
                  (sum, value) => sum + Number(value || 0),
                  0,
                );
                return formatCurrency(total);
              },
            },
          },
        },
      },
    },
    legend: {
      position: "bottom",
      offsetY: 0,
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
  };

  // Product Data
  const productOptions = {
    ...chartOptions,
    labels: productData.labels,
    colors: ["#0EA5E9", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"],
  };

  // Category Data
  const categorySeries = categoryData.series;
  const categoryOptions = {
    ...chartOptions,
    labels: categoryData.labels,
    colors: ["#06B6D4", "#10B981", "#F59E0B", "#D946EF", "#F43F5E"],
    tooltip: {
      y: {
        formatter: function (value, { seriesIndex, w }) {
          const qty = categoryData.qtys[seriesIndex];
          return `${formatCurrency(value)} (${qty} QTY)`;
        },
      },
    },
  };

  // Payment Data
  const paymentSeries = paymentData.series;
  const paymentOptions = {
    ...chartOptions,
    labels: paymentData.labels,
    colors: ["#0EA5E9", "#10B981", "#F59E0B", "#F472B6", "#8B5CF6"],
    tooltip: {
      y: {
        formatter: function (value, { seriesIndex, w }) {
          const qty = paymentData.qtys[seriesIndex];
          return `${formatCurrency(value)} (${qty} Trx)`;
        },
      },
    },
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {/* Produk Terlaris */}
      <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200/70 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 ring-1 ring-sky-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <rect x="7" y="12" width="3" height="6" />
              <rect x="12" y="8" width="3" height="10" />
              <rect x="17" y="5" width="3" height="13" />
            </svg>
          </div>
          <h3 className="font-bold text-slate-900">Produk Terlaris</h3>
        </div>
        <div className="h-72 flex flex-col justify-center gap-3 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Memuat data...
            </div>
          ) : productData.items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              Tidak ada data
            </div>
          ) : (
            productData.items.map((item, idx) => {
              const pct =
                productData.max > 0 ? (item.qty / productData.max) * 100 : 0;
              const colors = [
                "bg-sky-500",
                "bg-emerald-500",
                "bg-amber-500",
                "bg-violet-500",
                "bg-pink-500",
              ];
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-slate-700 truncate max-w-[50%]">
                      {item.label}
                    </span>
                    <span className="text-xs font-semibold text-slate-500 ml-2 text-right whitespace-nowrap">
                      {item.qty} Terjual{" "}
                      <span className="font-normal text-slate-400">
                        ({formatCurrency(item.value)})
                      </span>
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className={`${colors[idx % colors.length]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Peringkat Kategori */}
      <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200/70 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </div>
          <h3 className="font-bold text-slate-900">Kategori Terlaris</h3>
        </div>
        <div className="h-72">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Memuat data...
            </div>
          ) : (
            <Chart
              options={categoryOptions}
              series={categorySeries}
              type="donut"
              height="100%"
            />
          )}
        </div>
      </div>

      {/* Rank Pembayaran */}
      <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200/70 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 ring-1 ring-violet-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <h3 className="font-bold text-slate-900">Metode Pembayaran</h3>
        </div>
        <div className="h-72">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Memuat data...
            </div>
          ) : (
            <Chart
              options={paymentOptions}
              series={paymentSeries}
              type="donut"
              height="100%"
            />
          )}
        </div>
      </div>

      {error && (
        <div className="md:col-span-3 text-sm text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-lg px-4 py-3">
          {error}
        </div>
      )}
    </div>
  );
}
